<template>
  <v-dialog
    class="dialog-quotation"
    hide-overlay
    v-model="dialog"
    fullscreen
    @keydown.esc="onDialogClose"
    persistent
    scrollable
  >
    <dialog-customer-list
      :dialog="dialogCompanyList"
      :title="companyListDialogTitle"
      :is_edit="true"
      v-on:dialog-close="onDialogCompanyListClose"
    ></dialog-customer-list>

    <!-- Dialog for Comment Message Box -->
    <dialog-message-box
      :dialog="dialogMessageBox"
      v-model="quotation"
      :title="messageBoxTitle"
      :quotation_id="messageBoxQuotationId"
      :invoice_id="messageBoxInvoiceId"
      :company_id="messageBoxCompanyId"
      :company_name="messageBoxCompanyName"
      :category="messageBoxCategory"
      :tags="messageBoxTags"
      v-on:dialog-close="onDialogCloseMessageBox"
    ></dialog-message-box>

    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      :top="true"
    >
      {{ snackbarMsg }}
      <v-btn dark text @click="showSnackbar = false">CLOSE</v-btn>
    </v-snackbar>

    <!-- warning dialog -->
    <v-dialog v-model="dialogChangeCompanyWarning" max-width="650">
      <v-card
        v-if="new_quotation_company != null && quotation != null"
        color="white"
        tile
        flat
      >
        <v-card-title class="dialog-header-title">Assign Company</v-card-title>
        <v-alert v-model="alert" type="error" dismissible>{{ alertMessage }}</v-alert>
        <v-card-text class="mt-3">
          <v-alert tile outlined type="info" border="left" dense>
            <span>
              Assign company
              <strong>{{ new_quotation_company.name }}</strong> to this quotation
              <strong>{{ quotation.quotation_id }}</strong>
            </span>
            <br />
            <br />
            <span>Do you want to proceed?</span>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-row justify="end" class="mb-2">
            <v-btn
              class="mx-7 font-weight-bold"
              color="primary"
              depressed
              tile
              @click="changeQuotationCompany()"
              >YES</v-btn
            >
            <v-btn
              class="mx-7 font-weight-bold"
              color="error"
              tile
              depressed
              @click="dialogChangeCompanyWarning = false"
              >NO</v-btn
            >
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End warning dialog -->

    <!-- On Quotation Request Message -->
    <v-dialog v-model="dialogQuotationRequestMsg" persistent max-width="650">
      <v-card flat tile color="white" v-if="quotationRequestMsg != null">
        <v-card-text class="pt-4 px-2 pb-1">
          <v-alert outlined type="info" prominent border="left">
            <v-row class="title"
              >The following rate(s) are not availabe in the pricing table:</v-row
            >
            <v-row>
              <ul>
                <li
                  v-for="(msg, i) in quotationRequestMsg"
                  :key="i"
                  class="text-lg-caption"
                >
                  {{ msg }}
                </li>
              </ul>
            </v-row>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-row justify="end">
            <v-btn
              tile
              small
              depressed
              color="error"
              class="font-weight-font text-button"
              @click="dialogQuotationRequestMsg = false"
              >CLOSE</v-btn
            >
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card flat tile class="platform-font dialog-container">
      <v-tabs
        background-color="dialog-header-title"
        dark
        class="dm-quotation-tabs"
        v-model="active"
      >
        <v-tooltip bottom v-if="quotation != null">
          <template v-slot:activator="{ on }">
            <v-chip
              v-on="on"
              class="mt-3 ml-4 mr-12"
              v-if="quotation != null && $root.is_admin"
              small
              outlined
              label
              dark
              @click="onOpenCompanyList"
            >
              <span class="font-weight-bold caption text-uppercase">
                {{ quotation.company.short_name }} -
                {{ quotation.currency }}
              </span>
            </v-chip>
          </template>
          {{ quotation.company.name }} ({{ quotation.company_id }}) click to change
          company
        </v-tooltip>

        <v-tab :key="1" class="ml-12 font-weight-bold">Shipment</v-tab>
        <v-tab class="font-weight-bold" :key="2">Add-ons</v-tab>
        <v-tab class="font-weight-bold" :key="3">Files</v-tab>
        <v-tab class="font-weight-bold" :key="4">Financials</v-tab>
        <v-tab class="font-weight-bold" :key="5">Status</v-tab>
        <v-tab class="font-weight-bold" :key="6">Overview</v-tab>
      </v-tabs>

      <v-tabs-items v-model="active" v-if="isFreightQuotation()">
        <!-- Shipment -->
        <v-tab-item :key="1">
          <v-card-text class="container" v-if="quotation != null">
            <v-row class="pb-2 pt-2" no-gutters>
              <v-col cols="6">
                <p class="component-header">
                  Shipment type
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" color="primary" size="18"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <span>FCL: Full Container Load (Containers)</span>
                    <br />
                    <span>LCL: Less Container Load (Boxes or Pallets)</span>
                  </v-tooltip>
                </p>
                <v-btn-toggle
                  class="v-btn-toggle-new-quotation"
                  mandatory
                  dense
                  v-model="quotation.quotation_type"
                >
                  <v-btn text :value="CONSTANT.QUOTATION_TYPE_SEA_FCL" disabled
                    >Sea Freight FCL</v-btn
                  >
                  <v-btn text :value="CONSTANT.QUOTATION_TYPE_SEA_LCL" disabled
                    >Sea Freight LCL</v-btn
                  >
                  <v-btn text :value="CONSTANT.QUOTATION_TYPE_AIR" disabled
                    >Air Freight</v-btn
                  >
                </v-btn-toggle>
              </v-col>

              <v-col cols="3">
                <p class="component-header">Transaction</p>
                <v-btn-toggle
                  mandatory
                  dense
                  class="v-btn-toggle-new-quotation"
                  v-model="quotation.transaction_type"
                >
                  <v-btn text value="EXPORT" disabled>Exporting</v-btn>
                  <v-btn text value="IMPORT" disabled>Importing</v-btn>
                </v-btn-toggle>
              </v-col>

              <v-col cols="3">
                <p class="component-header">Incoterm</p>
                <v-select
                  class="form-control"
                  :items="sortedIncotermList"
                  item-text="label"
                  return-object
                  placeholder="Select shipping term"
                  solo
                  flat
                  v-model="quotation.incoterm"
                ></v-select>
              </v-col>
            </v-row>

            <!-- FCL Specifications -->
            <v-row
              no-gutters
              class="inset-row input-row"
              v-if="quotation.quotation_type == CONSTANT.QUOTATION_TYPE_SEA_FCL"
            >
              <v-col>
                <template v-for="(item, i) in quotation.containers">
                  <v-row no-gutters :key="i" class="mb-3">
                    <v-col cols="2" style="padding: 0 100px 0 0px">
                      <p class="component-header">Quantity</p>
                      <input
                        type="number"
                        class="form-control"
                        min="1"
                        id="containerQuantity"
                        v-model="item.container_quantity"
                      />
                    </v-col>

                    <v-col cols="3">
                      <p class="component-header">Container size</p>
                      <v-btn-toggle
                        mandatory
                        class="v-btn-toggle-new-quotation"
                        dense
                        v-model="item.container_size"
                      >
                        <v-btn text value="20">20"</v-btn>
                        <v-btn text value="40">40"</v-btn>
                      </v-btn-toggle>
                    </v-col>

                    <v-col cols="3" style="padding: 0 20px 0 0">
                      <p class="component-header">Container type</p>
                      <v-btn-toggle
                        mandatory
                        class="v-btn-toggle-new-quotation"
                        dense
                        v-model="item.container_type"
                      >
                        <v-btn text value="DRY">Dry</v-btn>
                        <v-btn text value="REEFER">Reefer</v-btn>
                        <v-btn text value="OT">Open Top</v-btn>
                      </v-btn-toggle>
                    </v-col>

                    <v-col>
                      <v-row class="pt-5" justify="end">
                        <v-btn
                          tile
                          outlined
                          class="font-weight-bold mr-4"
                          color="primary"
                          @click="onAddContainer"
                          >Add</v-btn
                        >
                        <v-btn
                          tile
                          outlined
                          class="font-weight-bold"
                          color="error"
                          @click="onRemoveContainer(item)"
                          :disabled="quotation.containers.length < 2"
                          >Delete</v-btn
                        >
                      </v-row>
                    </v-col>
                  </v-row>
                </template>
              </v-col>
            </v-row>

            <!-- LCL or AIR Speficiations -->
            <v-row class="inset-row input-row" v-else no-gutters>
              <v-col>
                <template v-for="(item, i) in quotation.cargo_units">
                  <v-row :key="i">
                    <v-col cols="2">
                      <p class="component-header">Quantity</p>
                      <input
                        type="number"
                        class="form-control"
                        min="1"
                        v-model.number="item.quantity"
                        id="containerQuantity"
                        style="max-width: 80px"
                      />
                    </v-col>

                    <v-col class="px-1">
                      <p class="component-header">Packaging type</p>
                      <v-select
                        class="form-control"
                        :items="lcl_packaging_type"
                        placeholder="Select packaging type"
                        solo
                        flat
                        style="max-width: 150px"
                        v-model="item.type"
                      ></v-select>
                    </v-col>

                    <v-col class="px-1">
                      <p class="component-header">Gross weight/unit (kg or lbs)</p>
                      <div class="input-group" style="flex-flow: row wrap">
                        <input
                          type="number"
                          class="form-control"
                          style="max-width: 120px"
                          min="0"
                          v-model.number="item.total_weight"
                        />
                        <v-select
                          class="custom-select form-control"
                          :items="lcl_weight_unit"
                          placeholder="kg"
                          solo
                          flat
                          style="max-width: 90px"
                          v-model="item.weight_unit"
                        ></v-select>
                      </div>
                    </v-col>

                    <v-col class="px-1">
                      <p class="component-header">Cubic meter/unit</p>
                      <div class="input-group" style="flex-flow: row wrap">
                        <input
                          type="number"
                          class="form-control"
                          style="max-width: 100px"
                          v-model.number="item.total_cubic_meters"
                        />
                      </div>
                    </v-col>

                    <v-col>
                      <v-row class="pt-6">
                        <v-btn
                          tile
                          outlined
                          class="font-weight-bold mr-4"
                          color="primary"
                          @click="onAddCargoUnit"
                          >Add</v-btn
                        >
                        <v-btn
                          tile
                          outlined
                          class="font-weight-bold"
                          color="error"
                          @click="onRemoveCargoUnit(item)"
                          :disabled="quotation.cargo_units.length < 2"
                          >Delete</v-btn
                        >
                      </v-row>
                    </v-col>
                  </v-row>
                </template>
              </v-col>
            </v-row>

            <!-- Port query -->
            <v-row class="pl-3 pb-4 pt-2" no-gutters>
              <v-col cols="6">
                <p class="component-header">Origin</p>
                <v-autocomplete
                  class="form-control autocomplete-port"
                  solo
                  flat
                  placeholder="Select origin port"
                  :items="sortedPortList"
                  v-model="quotation.origin_port"
                  item-text="label"
                  clearable
                  return-object
                >
                  <template v-slot:item="data">
                    <v-list-item-content style="font-size: 90%">
                      <v-list-item-title v-html="data.item.label"></v-list-item-title>
                    </v-list-item-content>
                  </template>
                  <template v-slot:item="data">
                    <img
                      :src="CONSTANT.getFlag(data.item.code_country)"
                      class="country-flag"
                    />
                    <v-list-item-content style="font-size: 90%">
                      <v-list-item-title v-html="data.item.label"></v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="6">
                <p class="component-header">Destination</p>
                <v-autocomplete
                  class="form-control autocomplete-port"
                  solo
                  flat
                  placeholder="Select destination port"
                  :items="sortedPortList"
                  v-model="quotation.destination_port"
                  item-text="label"
                  clearable
                  return-object
                >
                  <template v-slot:item="data">
                    <v-list-item-content style="font-size: 90%">
                      <v-list-item-title v-html="data.item.label"></v-list-item-title>
                    </v-list-item-content>
                  </template>
                  <template v-slot:item="data">
                    <img
                      :src="CONSTANT.getFlag(data.item.code_country)"
                      class="country-flag"
                    />
                    <v-list-item-content style="font-size: 90%">
                      <v-list-item-title v-html="data.item.label"></v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Date and cargo type query -->
            <v-row no-gutters class="pl-3 pb-4">
              <v-col cols="3">
                <p class="component-header">Cargo ready date</p>
                <flat-pickr
                  v-model="quotation.cargo_ready_date"
                  class="form-control"
                  style="max-width: 200px; background: white"
                  placeholder="Select date"
                  :config="config"
                  name="date"
                ></flat-pickr>
              </v-col>

              <v-col cols="3">
                <p class="component-header">DG-Classification</p>
                <v-select
                  :items="cargoTypeList"
                  placeholder="Select cargo type"
                  class="form-control"
                  solo
                  flat
                  style="max-width: 200px"
                  v-model="quotation.cargo_type"
                  item-text="code"
                  item-value="code"
                  return-object
                ></v-select>
              </v-col>

              <v-col cols="3">
                <p class="component-header">Cargo value</p>
                <div class="input-group">
                  <v-select
                    class="custom-select form-control"
                    :items="cargo_value_items"
                    solo
                    flat
                    style="max-width: 90px"
                    v-model="quotation.cargo_value_currency"
                  ></v-select>

                  <input
                    type="number"
                    style="max-width: 140px"
                    class="form-control"
                    placeholder="Cargo value"
                    v-model="quotation.cargo_value"
                  />
                </div>
              </v-col>

              <v-col cols="3">
                <p class="component-header">HS code</p>
                <div class="input-group">
                  <input
                    type="text"
                    style="width: 50px"
                    class="form-control"
                    placeholder="Example: 40116900"
                    v-model="quotation.hs_code"
                  />
                </div>
              </v-col>
            </v-row>

            <!-- Commodity input -->
            <v-row no-gutters class="pl-3">
              <v-col cols="4">
                <div class="input-group group-12">
                  <label for>Commodity</label>
                  <textarea
                    class="form-control"
                    id="exampleFormControlTextarea1"
                    rows="2"
                    v-model="quotation.commodity"
                    style="padding: 5px"
                  ></textarea>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <!-- Add-ons -->
        <v-tab-item :key="2">
          <v-row no-gutters>
            <v-col>
              <v-card flat color="white">
                <v-card-text class="container px-0" v-if="quotation != null">
                  <v-row no-gutters v-if="quotation.incoterm != null">
                    <v-col class="addon-main-col black--text mr-6">
                      <v-row dense class="pl-2">
                        <v-col>
                          <h4 class="title">Customs Clearance</h4>
                        </v-col>
                      </v-row>

                      <v-row class="addon-main-col-row" no-gutters>
                        <v-col>
                          <v-row v-if="show_export_clearance">
                            <v-checkbox
                              hide-details
                              dense
                              v-model="quotation.include_customs_clearance_export"
                              :disabled="quotation.origin_port == null"
                            >
                              <template v-slot:label>
                                <span
                                  class="black--text"
                                  v-if="quotation.origin_port != null"
                                  >Include export customs handling at
                                  <b>{{ quotation.origin_port.name }}</b></span
                                >

                                <span v-else>Origin port not selected</span>
                              </template>
                            </v-checkbox>
                          </v-row>

                          <v-row v-else no-gutters>
                            <v-col class="px-3">
                              <v-alert
                                dense
                                text
                                border="left"
                                type="info"
                                class="body-2"
                                tile
                                color="primary"
                              >
                                Export clearance is not required for your selected
                                transaction and incoterm
                              </v-alert>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-divider></v-divider>
                          </v-row>

                          <v-row v-if="show_import_clearance">
                            <v-checkbox
                              hide-details
                              dense
                              v-model="quotation.include_customs_clearance_import"
                              :disabled="quotation.destination_port == null"
                            >
                              <template v-slot:label>
                                <span
                                  class="black--text"
                                  v-if="quotation.destination_port != null"
                                  >Include import customs handling at
                                  <b>{{ quotation.destination_port.name }}</b></span
                                >

                                <span v-else>Destination port not selected</span>
                              </template>
                            </v-checkbox>

                            <!-- <div class="af-checkbox">
                              <input
                                type="checkbox"
                                id="customCheck2"
                                :disabled="quotation.destination_port == null"
                                v-model="
                                  quotation.include_customs_clearance_import
                                "
                              />
                              <label
                                class
                                for="customCheck2"
                                v-if="quotation.destination_port != null"
                              >
                                Include import customs handling at
                                <b>{{ quotation.destination_port.name }}</b>
                              </label>
                              <label v-else
                                >Destination port not selected</label
                              >
                            </div> -->
                          </v-row>

                          <v-row v-else no-gutters>
                            <v-col class="px-3">
                              <v-alert
                                dense
                                text
                                border="left"
                                type="info"
                                class="body-2"
                                tile
                                color="primary"
                              >
                                Import clearance is not required for your selected
                                transaction and incoterm
                              </v-alert>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>

                      <!-- Haulage -->
                      <v-row
                        no-gutters
                        class="addon-option-1 pt-4"
                        v-if="quotation.quotation_type == CONSTANT.QUOTATION_TYPE_SEA_FCL"
                      >
                        <v-col>
                          <v-row class="pl-2">
                            <h4 class="title mr-2">Haulage</h4>
                            <v-tooltip right>
                              <template v-slot:activator="{ on }">
                                <img
                                  v-on="on"
                                  data-toggle="tooltip"
                                  src="@/assets/svg/tooltip.svg"
                                  alt="Customs Clearance"
                                  class="tooltip-image pb-1"
                                />
                              </template>
                              Service for delivering container(s) to location for
                              loading/unloading and pull back to container yard at port
                            </v-tooltip>
                          </v-row>

                          <v-row class="addon-main-col-row pt-2" no-gutters>
                            <v-col>
                              <v-row no-gutters class="pb-2">
                                <!-- Export Haulage -->
                                <v-col v-if="show_export_haulage">
                                  <v-row class="pl-3">
                                    <span
                                      class="font-weight-bold primary--text subtitle-2"
                                      >Export Haulage</span
                                    >
                                  </v-row>

                                  <v-row>
                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="quotation.include_haulage_export"
                                    >
                                      <template v-slot:label>
                                        <span class="black--text">Include haulage</span>
                                      </template>
                                    </v-checkbox>

                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="quotation.include_haulage_sdl_export"
                                      :disabled="!quotation.include_haulage_export"
                                    >
                                      <template v-slot:label>
                                        <span
                                          :class="
                                            quotation.include_haulage_export
                                              ? 'text--primary black--text'
                                              : 'text--disabled'
                                          "
                                          >Side loader</span
                                        >
                                      </template>
                                    </v-checkbox>

                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="
                                        quotation.use_predefined_haulage_exporter_location
                                      "
                                      :disabled="!quotation.include_haulage_export"
                                    >
                                      <template v-slot:label>
                                        <span
                                          :class="
                                            quotation.include_haulage_export
                                              ? 'text--primary black--text'
                                              : 'text--disabled'
                                          "
                                          >Use predefined locations</span
                                        >
                                      </template>
                                    </v-checkbox>
                                  </v-row>

                                  <v-row
                                    class="px-4"
                                    v-show="
                                      quotation.use_predefined_haulage_exporter_location
                                    "
                                  >
                                    <label>Haulage city:</label>
                                    <v-autocomplete
                                      class="form-control"
                                      solo
                                      flat
                                      placeholder="Select city"
                                      :items="sortedHaulageExportCitiesList"
                                      item-text="name"
                                      v-model="quotation.city_haulage_export"
                                      :disabled="!quotation.include_haulage_export"
                                      clearable
                                      return-object
                                    ></v-autocomplete>
                                  </v-row>

                                  <v-row
                                    no-gutters
                                    class="px-4"
                                    v-show="
                                      !quotation.use_predefined_haulage_exporter_location
                                    "
                                  >
                                    <v-col>
                                      <v-row>
                                        <label>Haulage address:</label>
                                      </v-row>
                                      <v-row no-gutters>
                                        <v-col>
                                          <input
                                            class="form-control rounded-0"
                                            id="haulage-exporter-autocomplete"
                                            :disabled="!quotation.include_haulage_export"
                                            v-model="
                                              quotation.exporter_location
                                                .formatted_address
                                            "
                                          />
                                        </v-col>
                                        <v-col
                                          cols="1"
                                          v-if="
                                            quotation.include_haulage_export &&
                                            quotation.exporter_location
                                              .formatted_address != null &&
                                            quotation.exporter_location
                                              .formatted_address != '' &&
                                            quotation.exporter_location.place_id == null
                                          "
                                        >
                                          <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                              <v-icon v-on="on" color="error" size="20"
                                                >mdi-alert-circle</v-icon
                                              >
                                            </template>
                                            Location entered could not be geocoded.
                                          </v-tooltip>
                                        </v-col>
                                      </v-row>
                                      <v-row>
                                        <span
                                          v-if="
                                            quotation.exporter_location
                                              .formatted_address != null &&
                                            quotation.exporter_location
                                              .formatted_address != ''
                                          "
                                          class="font-italic pl-1"
                                        >
                                          {{ quotation.exporter_location.name }}
                                        </span>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </v-col>

                                <v-col v-else class="px-3">
                                  <v-alert
                                    dense
                                    text
                                    border="left"
                                    type="info"
                                    class="body-2"
                                    tile
                                    color="primary"
                                  >
                                    Export haulage is not required for your selected
                                    transaction and incoterm
                                  </v-alert>
                                </v-col>
                              </v-row>

                              <v-row>
                                <v-divider></v-divider>
                              </v-row>

                              <v-row no-gutters>
                                <!-- Import Haulage -->
                                <v-col v-if="show_import_haulage">
                                  <v-row class="pl-3">
                                    <span
                                      class="font-weight-bold primary--text subtitle-2"
                                      >Import Haulage</span
                                    >
                                  </v-row>

                                  <v-row>
                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="quotation.include_haulage_import"
                                    >
                                      <template v-slot:label>
                                        <span class="black--text">Include haulage</span>
                                      </template>
                                    </v-checkbox>

                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="quotation.include_haulage_sdl_import"
                                      :disabled="!quotation.include_haulage_import"
                                    >
                                      <template v-slot:label>
                                        <span
                                          :class="
                                            quotation.include_haulage_import
                                              ? 'text--primary'
                                              : 'text--disabled'
                                          "
                                          >Side loader</span
                                        >
                                      </template>
                                    </v-checkbox>

                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="
                                        quotation.use_predefined_haulage_importer_location
                                      "
                                      :disabled="!quotation.include_haulage_import"
                                    >
                                      <template v-slot:label>
                                        <span
                                          :class="
                                            quotation.include_haulage_import
                                              ? 'text--primary black--text'
                                              : 'text--disabled'
                                          "
                                          >Use predefined locations</span
                                        >
                                      </template>
                                    </v-checkbox>
                                  </v-row>

                                  <v-row
                                    class="px-4"
                                    v-show="
                                      quotation.use_predefined_haulage_importer_location
                                    "
                                  >
                                    <label>Haulage city:</label>
                                    <v-autocomplete
                                      class="form-control"
                                      solo
                                      flat
                                      placeholder="Select city"
                                      :items="sortedHaulageImportCitiesList"
                                      item-text="name"
                                      v-model="quotation.city_haulage_import"
                                      :disabled="!quotation.include_haulage_import"
                                      clearable
                                      return-object
                                    ></v-autocomplete>
                                  </v-row>

                                  <v-row
                                    no-gutters
                                    class="px-4"
                                    v-show="
                                      !quotation.use_predefined_haulage_importer_location
                                    "
                                  >
                                    <v-col>
                                      <v-row>
                                        <label>Haulage address:</label>
                                      </v-row>
                                      <v-row no-gutters>
                                        <v-col>
                                          <input
                                            class="form-control rounded-0"
                                            id="haulage-importer-autocomplete"
                                            :disabled="!quotation.include_haulage_import"
                                            v-model="
                                              quotation.importer_location
                                                .formatted_address
                                            "
                                          />
                                        </v-col>
                                        <v-col
                                          cols="1"
                                          v-if="
                                            quotation.include_haulage_import &&
                                            quotation.importer_location
                                              .formatted_address != null &&
                                            quotation.importer_location
                                              .formatted_address != '' &&
                                            quotation.importer_location.place_id == null
                                          "
                                        >
                                          <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                              <v-icon v-on="on" color="error" size="20"
                                                >mdi-alert-circle</v-icon
                                              >
                                            </template>
                                            Location entered could not be geocoded.
                                          </v-tooltip>
                                        </v-col>
                                      </v-row>
                                      <v-row>
                                        <span
                                          v-if="
                                            quotation.importer_location
                                              .formatted_address != null &&
                                            quotation.importer_location
                                              .formatted_address != ''
                                          "
                                          class="font-italic pl-1"
                                        >
                                          {{ quotation.importer_location.name }}
                                        </span>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </v-col>

                                <v-col v-else class="px-4">
                                  <v-alert
                                    dense
                                    text
                                    border="left"
                                    type="info"
                                    class="body-2"
                                    tile
                                    color="primary"
                                  >
                                    Import haulage is not required for your selected
                                    transaction and incoterm
                                  </v-alert>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>

                      <!-- Trucking -->
                      <v-row class="addon-option-1 pt-4" v-else no-gutters>
                        <v-col>
                          <v-row class="pl-2">
                            <h4 class="title mr-2">Trucking</h4>
                            <v-tooltip right>
                              <template v-slot:activator="{ on }">
                                <img
                                  v-on="on"
                                  data-toggle="tooltip"
                                  src="@/assets/svg/tooltip.svg"
                                  alt="Customs Clearance"
                                  class="tooltip-image pb-1"
                                />
                              </template>
                              Service for pick-up (or delivery) of cargo from (to)
                              location to (from) port
                            </v-tooltip>
                          </v-row>

                          <v-row class="addon-main-col-row pt-2" no-gutters>
                            <v-col>
                              <v-row no-gutters class="pb-2">
                                <!-- Export transport -->
                                <v-col v-if="show_export_transport">
                                  <v-row class="pl-3">
                                    <span
                                      class="font-weight-bold primary--text subtitle-2"
                                      >Export Trucking</span
                                    >
                                  </v-row>

                                  <v-row>
                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="quotation.include_transport_export"
                                    >
                                      <template v-slot:label>
                                        <span class="black--text">Include transport</span>
                                      </template>
                                    </v-checkbox>

                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="
                                        quotation.use_predefined_transport_exporter_location
                                      "
                                      :disabled="!quotation.include_transport_export"
                                    >
                                      <template v-slot:label>
                                        <span
                                          :class="
                                            quotation.include_transport_export
                                              ? 'text--primary black--text'
                                              : 'text--disabled'
                                          "
                                          >Use predefined locations</span
                                        >
                                      </template>
                                    </v-checkbox>
                                  </v-row>

                                  <v-row
                                    class="px-4"
                                    v-show="
                                      quotation.use_predefined_transport_exporter_location
                                    "
                                  >
                                    <label>Transport city:</label>
                                    <v-autocomplete
                                      class="form-control"
                                      solo
                                      flat
                                      placeholder="Select city"
                                      :items="sortedTransportExportCitiesList"
                                      item-text="name"
                                      v-model="quotation.city_transport_export"
                                      :disabled="!quotation.include_transport_export"
                                      clearable
                                      return-object
                                    ></v-autocomplete>
                                  </v-row>

                                  <v-row
                                    no-gutters
                                    class="px-4"
                                    v-show="
                                      !quotation.use_predefined_transport_exporter_location
                                    "
                                  >
                                    <v-col>
                                      <v-row>
                                        <label>Haulage address:</label>
                                      </v-row>
                                      <v-row no-gutters>
                                        <v-col>
                                          <input
                                            class="form-control rounded-0"
                                            id="transport-exporter-autocomplete"
                                            :disabled="
                                              !quotation.include_transport_export
                                            "
                                            v-model="
                                              quotation.exporter_location
                                                .formatted_address
                                            "
                                          />
                                        </v-col>
                                        <v-col
                                          cols="1"
                                          v-if="
                                            quotation.include_transport_export &&
                                            quotation.exporter_location
                                              .formatted_address != null &&
                                            quotation.exporter_location
                                              .formatted_address != '' &&
                                            quotation.exporter_location.place_id == null
                                          "
                                        >
                                          <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                              <v-icon v-on="on" color="error" size="20"
                                                >mdi-alert-circle</v-icon
                                              >
                                            </template>
                                            Location entered could not be geocoded.
                                          </v-tooltip>
                                        </v-col>
                                      </v-row>
                                      <v-row>
                                        <span
                                          v-if="
                                            quotation.exporter_location
                                              .formatted_address != null &&
                                            quotation.exporter_location
                                              .formatted_address != ''
                                          "
                                          class="font-italic pl-1"
                                        >
                                          {{ quotation.exporter_location.name }}
                                        </span>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </v-col>

                                <v-col v-else class="px-3">
                                  <v-alert
                                    dense
                                    text
                                    border="left"
                                    type="info"
                                    class="body-2"
                                    tile
                                    color="primary"
                                  >
                                    Export transport is not required for your selected
                                    transaction and incoterm
                                  </v-alert>
                                </v-col>
                              </v-row>

                              <v-row>
                                <v-divider></v-divider>
                              </v-row>

                              <v-row no-gutters>
                                <!-- Import transport -->
                                <v-col v-if="show_import_transport">
                                  <v-row class="pl-3">
                                    <span
                                      class="font-weight-bold primary--text subtitle-2"
                                      >Import Trucking</span
                                    >
                                  </v-row>

                                  <v-row>
                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="quotation.include_transport_import"
                                    >
                                      <template v-slot:label>
                                        <span class="black--text">Include transport</span>
                                      </template>
                                    </v-checkbox>

                                    <v-checkbox
                                      hide-details
                                      dense
                                      v-model="
                                        quotation.use_predefined_transport_importer_location
                                      "
                                      :disabled="!quotation.include_transport_import"
                                    >
                                      <template v-slot:label>
                                        <span
                                          :class="
                                            quotation.include_transport_import
                                              ? 'text--primary black--text'
                                              : 'text--disabled'
                                          "
                                          >Use predefined locations</span
                                        >
                                      </template>
                                    </v-checkbox>
                                  </v-row>

                                  <v-row
                                    class="px-4"
                                    v-show="
                                      quotation.use_predefined_transport_importer_location
                                    "
                                  >
                                    <label>Transport city:</label>
                                    <v-autocomplete
                                      class="form-control"
                                      solo
                                      flat
                                      placeholder="Select city"
                                      :items="sortedTransportImportCitiesList"
                                      item-text="name"
                                      v-model="quotation.city_transport_import"
                                      :disabled="!quotation.include_transport_import"
                                      clearable
                                      return-object
                                    ></v-autocomplete>
                                  </v-row>

                                  <v-row
                                    no-gutters
                                    class="px-4"
                                    v-show="
                                      !quotation.use_predefined_transport_importer_location
                                    "
                                  >
                                    <v-col>
                                      <v-row>
                                        <label>Haulage address:</label>
                                      </v-row>
                                      <v-row no-gutters>
                                        <v-col>
                                          <input
                                            class="form-control rounded-0"
                                            id="transport-importer-autocomplete"
                                            :disabled="
                                              !quotation.include_transport_import
                                            "
                                            v-model="
                                              quotation.importer_location
                                                .formatted_address
                                            "
                                          />
                                        </v-col>
                                        <v-col
                                          cols="1"
                                          v-if="
                                            quotation.include_transport_import &&
                                            quotation.importer_location
                                              .formatted_address != null &&
                                            quotation.importer_location
                                              .formatted_address != '' &&
                                            quotation.importer_location.place_id == null
                                          "
                                        >
                                          <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                              <v-icon v-on="on" color="error" size="20"
                                                >mdi-alert-circle</v-icon
                                              >
                                            </template>
                                            Location entered could not be geocoded.
                                          </v-tooltip>
                                        </v-col>
                                      </v-row>
                                      <v-row>
                                        <span
                                          v-if="
                                            quotation.importer_location
                                              .formatted_address != null &&
                                            quotation.importer_location
                                              .formatted_address != ''
                                          "
                                          class="font-italic pl-1"
                                        >
                                          {{ quotation.importer_location.name }}
                                        </span>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </v-col>

                                <v-col v-else class="px-3">
                                  <v-alert
                                    dense
                                    text
                                    border="left"
                                    type="info"
                                    class="body-2"
                                    tile
                                    color="primary"
                                  >
                                    Import transport is not required for your selected
                                    transaction and incoterm
                                  </v-alert>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-col>

                    <v-col cols="5" class="black--text addon-sub-col pl-4 pt-10">
                      <!-- <v-row >
                      <v-checkbox
                        hide-details
                        dense
                        v-model="quotation.include_letter_of_credit_handling"
                      >
                        <template v-slot:label>
                          <span class="black--text pr-2"
                            >Include letter of credit handling</span
                          >
                          <v-tooltip right>
                            <template v-slot:activator="{ on }">
                              <img
                                v-on="on"
                                data-toggle="tooltip"
                                src="@/assets/svg/tooltip.svg"
                                alt
                                class="tooltip-image"
                              />
                            </template>
                            Check here if a letter of credit is the agreed form of
                            transaction between shipper and consignee.
                            <br />The letter of credit will be required to process
                            the bill-of-lading.
                          </v-tooltip>
                        </template>
                      </v-checkbox>
                    </v-row> -->

                      <v-row class="pb-3" v-show="show_include_telex_release">
                        <v-checkbox
                          hide-details
                          dense
                          v-model="quotation.include_telex_release"
                        >
                          <template v-slot:label>
                            <span class="black--text pr-2">Include telex release</span>
                            <v-tooltip right>
                              <template v-slot:activator="{ on }">
                                <img
                                  v-on="on"
                                  data-toggle="tooltip"
                                  src="@/assets/svg/tooltip.svg"
                                  alt
                                  class="tooltip-image"
                                />
                              </template>
                              Delivery of bill-of-lading through telex release service.
                              Telex release facility will be charged on both
                              <br />the exporting and importing party. Do verify that this
                              is the agreed method of delivering the bill-of-lading
                              between both parties.
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-row>

                      <v-row class="py-3" v-show="show_insurance">
                        <v-checkbox
                          hide-details
                          dense
                          v-model="quotation.include_insurance"
                        >
                          <template v-slot:label>
                            <span class="black--text">Include insurance</span>
                          </template>
                        </v-checkbox>
                      </v-row>

                      <v-row class="py-3">
                        <v-checkbox
                          hide-details
                          dense
                          v-model="quotation.include_other_services"
                        >
                          <template v-slot:label>
                            <span class="black--text">Include additional services</span>
                          </template>
                        </v-checkbox>
                      </v-row>

                      <v-row class="py-2 pl-4">
                        <textarea
                          class="form-control"
                          id="exampleFormControlTextarea2"
                          rows="6"
                          v-model="quotation.other_services"
                        ></textarea>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Files -->
        <v-tab-item :key="3">
          <v-card-text v-if="quotation != null">
            <quotation-file-upload v-model="quotation"></quotation-file-upload>
          </v-card-text>
        </v-tab-item>

        <!-- Financial Components -->
        <v-tab-item :key="4">
          <v-row no-gutters class="pt-4">
            <v-col cols="5" class="pl-3">
              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    small
                    depressed
                    tile
                    color="primary"
                    class="font-weight-bold px-1"
                    dark
                    @click="onRecalculate"
                  >
                    <v-icon size="20">mdi-calculator</v-icon>RE-CALCULATE
                  </v-btn>
                </template>
                Re-calculate estimates
              </v-tooltip>

              <v-btn
                v-if="quotation.quotation_type != 'FCL'"
                small
                tile
                outlined
                color="secondary"
                class="font-weight-bold text-sm-caption px-1 ml-2"
                @click="onRefreshQty"
                >REFRESH QTY</v-btn
              >

              <v-btn
                small
                tile
                outlined
                color="primary"
                class="font-weight-bold text-sm-caption px-1 ml-2"
                @click="addNewComponent"
                >ADD ITEM</v-btn
              >

              <v-btn
                small
                tile
                outlined
                color="error"
                class="font-weight-bold text-sm-caption px-1 ml-2"
                @click="addDiscountComponent"
                >ADD DISCOUNT</v-btn
              >
            </v-col>

            <v-col
              class="pr-6 primary--text"
              v-if="
                quotation != null &&
                quotation.quotation_type != CONSTANT.QUOTATION_TYPE_SEA_FCL
              "
            >
              <v-row justify="space-between">
                <span class="caption"
                  >Gross weight/unit: {{ quotation.total_weight }} kg</span
                >
                <span class="caption"
                  >Cubic meter/unit: {{ quotation.total_cubic_meters }}&#13221;</span
                >
                <span
                  v-if="quotation.quotation_type == CONSTANT.QUOTATION_TYPE_AIR"
                  class="caption"
                  >Total chargeable weight: {{ quotation.quantity }} kg</span
                >
              </v-row>
            </v-col>
          </v-row>

          <v-row class="tab-container pt-1 px-2">
            <financial-component-table
              v-model="quotation"
              :show-cost="true"
            ></financial-component-table>
          </v-row>

          <v-row justify="start" class="pl-5">
            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  small
                  depressed
                  tile
                  color="primary"
                  class="font-weight-bold px-1"
                  dark
                  @click="onRecalculate"
                >
                  <v-icon size="20">mdi-calculator</v-icon>RE-CALCULATE
                </v-btn>
              </template>
              Re-calculate estimates
            </v-tooltip>

            <v-btn
              v-if="quotation.quotation_type != 'FCL'"
              small
              tile
              outlined
              color="secondary"
              class="font-weight-bold text-sm-caption px-1 ml-2"
              @click="onRefreshQty"
              >REFRESH QTY</v-btn
            >

            <v-btn
              small
              tile
              outlined
              color="primary"
              class="font-weight-bold text-sm-caption px-1 ml-2"
              @click="addNewComponent"
              >ADD ITEM</v-btn
            >

            <v-btn
              small
              tile
              outlined
              color="error"
              class="font-weight-bold text-sm-caption px-1 ml-2"
              @click="addDiscountComponent"
              >ADD DISCOUNT</v-btn
            >
          </v-row>
        </v-tab-item>

        <!-- Status -->
        <v-tab-item :key="5">
          <v-card-text class="container" v-if="quotation != null">
            <v-row>
              <v-col cols="8">
                <v-row class="pl-4">
                  <span class="headline primary--text">Quotation Status</span>
                </v-row>

                <v-row no-gutters class="pt-4 pl-4">
                  <v-select
                    class="mr-4"
                    :items="statusList"
                    solo
                    flat
                    style="max-width: 350px"
                    v-model="quotation.status"
                    item-text="label"
                    item-value="code"
                  ></v-select>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <!-- Overview -->
        <v-tab-item :key="6">
          <v-card-text class="container pb-0 mb-0" v-if="quotation != null">
            <v-row class="pl-4" no-gutters>
              <v-col>
                <span class="headline primary--text">Quotation Overview</span>
              </v-col>

              <v-col class="d-flex justify-end">
                <v-btn
                  class="font-weight-bold mr-4 px-2"
                  small
                  tile
                  depressed
                  color="secondary"
                  @click="onOverviewRefresh"
                >
                  <v-icon class="mr-1">mdi-refresh</v-icon>Refresh
                </v-btn>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-if="
                        quotation.has_complete_price ||
                        quotation.override_has_complete_price
                      "
                      v-on="on"
                      class="font-weight-bold px-2"
                      small
                      tile
                      depressed
                      color="primary"
                      @click="downloadPDF"
                    >
                      <v-icon class="mr-1">mdi-download</v-icon>Download as pdf
                    </v-btn>
                  </template>
                  Download PDF file for printing
                </v-tooltip>
              </v-col>
            </v-row>

            <v-row>
              <v-divider class="py-0 my-0"></v-divider>
            </v-row>

            <v-row>
              <quotation-overview :quotation="quotationOverview"></quotation-overview>
            </v-row>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>

      <v-divider></v-divider>

      <v-card-actions class="px-5 pb-0 mb-0" v-if="quotation != null">
        <v-row no-gutters>
          <v-col cols="4">
            <v-btn
              small
              tile
              class="font-weight-bold"
              color="primary"
              @click="onViewMessages"
            >
              <v-icon class="mr-2" size="20">mdi-message-text</v-icon>COMMENTS
            </v-btn>
          </v-col>

          <v-col>
            <v-row justify="end">
              <v-btn
                v-if="$root.is_super_admin && !quotation.has_shipment"
                tile
                depressed
                small
                class="body-2 font-weight-bold mx-4"
                color="secondary"
                @click="adminCreateShipment"
                >CREATE SHIPMENT</v-btn
              >

              <v-btn
                small
                depressed
                tile
                color="primary"
                class="body-2 font-weight-bold mx-4"
                @click="onSave"
                >SAVE</v-btn
              >

              <v-btn
                small
                depressed
                tile
                color="error"
                class="font-weight-bold mx-4"
                @click="onDialogClose"
                >CLOSE</v-btn
              >
            </v-row>
          </v-col>
        </v-row>
      </v-card-actions>

      <v-divider></v-divider>

      <v-card flat tile v-if="quotation != null">
        <v-card-subtitle
          class="text-center title primary--text font-weight-bold pt-0 pb-2"
          >Activity Log</v-card-subtitle
        >

        <v-card-text class="px-0">
          <activity-log :quotation_id="quotation.quotation_id"></activity-log>
        </v-card-text>
      </v-card>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import constant from "@/const";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import "flatpickr/dist/themes/airbnb.css";
import QuotationFileUpload from "@/components/shared/QuotationFileUpload.vue";
import ActivityLog from "@/components/shared/ActivityLog.vue";

export default {
  props: ["value", "dialog"],
  components: {
    flatPickr,
    QuotationFileUpload,
    ActivityLog,
  },

  data() {
    return {
      CONSTANT: constant,
      snackbarColor: "",
      showSnackbar: false,
      snackbarMsg: "",
      snackbarTimeout: 8000,
      alert: false,
      alertType: "success",
      alertMessage: "",

      config: {
        altInput: true,
        altInputClass: "flatpickr-alt-input",
        // enableTime: true,
        altFormat: "M j, Y",
        dateFormat: "Y-m-d",
        // time_24hr: true,
        position: "auto",
      },

      lcl_packaging_type: ["carton", "pallet", "box"],
      lcl_weight_unit: ["kg", "lbs"],
      cargo_value_items: ["USD", "MYR"],

      statusList: [
        { code: -1, label: "Expired" },
        { code: 1001, label: "Draft" },
        { code: 1002, label: "Request" },
        { code: 2001, label: "Proposed" },
        { code: 2002, label: "Price updated" },
        { code: 3001, label: "Confirmed" },
        { code: 4001, label: "Completed shipment delivery" },
        // { code: 4002, label: "Invoice issued" },
        { code: 5001, label: "Invoice paid" },
        { code: 10001, label: "Quotation closed" },
      ],

      active: 1,
      financialsActive: 1,
      quotation: null,
      quotationRequestMsg: null,
      dialogQuotationRequestMsg: false,

      updatedQuotation: null,
      quotationUpdated: false,
      quotationOverview: null,

      dialogCompanyList: false,
      companyListDialogTitle: null,
      isReAssigned: false,
      reAssignedCompany: null,
      new_quotation_company: null,

      dialogMessageBox: false,
      messageBoxTitle: null,
      messageBoxQuotationId: null,
      messageBoxInvoiceId: null,
      messageBoxCompanyId: null,
      messageBoxCompanyName: null,
      messageBoxCategory: "quotation",
      messageBoxTags: [],

      dialogChangeCompanyWarning: false,

      seaPortList: [],
      airPortList: [],
      incotermList: [],
      cargoTypeList: [],

      citiesHaulageExport: [],
      citiesHaulageImport: [],

      citiesTransportExport: [],
      citiesTransportImport: [],

      haulage_exporter_location: {
        name: null,
        formatted_address: null,
      },
      haulage_importer_location: {
        name: null,
        formatted_address: null,
      },
      transport_exporter_location: {
        name: null,
        formatted_address: null,
      },
      transport_importer_location: {
        name: null,
        formatted_address: null,
      },

      haulage_exporter_autocomplete: null,
      haulage_importer_autocomplete: null,
      transport_exporter_autocomplete: null,
      transport_importer_autocomplete: null,
    };
  },

  async created() {
    if (this.dialog) {
      this.initializeParameters();
      this.quotation = this.value;

      this.quotationUpdated = false;

      this.quotationOverview = JSON.parse(JSON.stringify(this.quotation));
    } else {
      this.quotation = null;
      this.quotationOverview = null;
    }
  },

  watch: {
    dialog() {
      console.log("dialog: ", this.dialog);
    },

    origin_port_watcher() {
      if (this.show_export_haulage || this.show_export_transport) {
        this.loadOriginCities(
          this.quotation.quotation_type,
          this.quotation.origin_port.un_code
        );
      }
    },

    destination_port_watcher() {
      if (this.show_import_haulage || this.show_import_transport) {
        this.loadDestinationCities(
          this.quotation.quotation_type,
          this.quotation.destination_port.un_code
        );
      }
    },
  },

  mounted() {},

  methods: {
    quotationExists() {
      return this.quotation != null;
    },

    isFreightQuotation() {
      if (!this.quotationExists()) {
        return false;
      }
      return (
        this.quotation.quotation_type == this.CONSTANT.QUOTATION_TYPE_SEA_FCL ||
        this.quotation.quotation_type == this.CONSTANT.QUOTATION_TYPE_SEA_LCL ||
        this.quotation.quotation_type == this.CONSTANT.QUOTATION_TYPE_AIR
      );
    },

    async initializeParameters() {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "Initialize parameters";

      let resp = await this.$https.get("/api/quotations/init");
      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.seaPortList = resp.data.data.supported_seaports;
        this.airPortList = resp.data.data.supported_airports;
        this.incotermList = resp.data.data.supported_incoterms;
        this.cargoTypeList = resp.data.data.supported_cargo_types;
      }
      this.$root.blockUIshow = false;

      // if show haulage export
      if (this.show_export_haulage) {
        // init haulage exporter address autocomplete
        this.initHaulageExporterAddressAutocomplete();
      }

      // if show haulage import
      if (this.show_import_haulage) {
        // init haulage importer address autocomplete
        this.initHaulageImporterAddressAutocomplete();
      }

      // if show transport export
      if (this.show_export_transport) {
        // init transport exporter address autocomplete
        this.initTransportExporterAddressAutocomplete();
      }

      // if show transport import
      if (this.show_import_transport) {
        // init transport importer address autocomplete
        this.initTransportImporterAddressAutocomplete();
      }
    },

    getFlag(flagName) {
      try {
        return require(`@/assets/flags/4x3/${flagName.toLowerCase()}.svg`);
      } catch (e) {
        return require("@/assets/flags/4x3/undefined.svg");
      }
    },

    /** Add a new container info */
    onAddContainer() {
      this.quotation.containers.push({
        container_quantity: 1,
        container_size: "20",
        container_type: "DRY",
      });

      // get container total
      this.quotation.container_total = this.quotation.containers.length;
    },

    /** Remove selected container info */
    onRemoveContainer(container) {
      this.quotation.containers = this.quotation.containers.filter(function (ele) {
        return ele != container;
      });

      // get container total
      this.quotation.container_total = this.quotation.containers.length;
    },

    /** Add a new cargo unit info */
    onAddCargoUnit() {
      this.quotation.cargo_units.push({
        dimension_unit: "m",
        total_cubic_meters: 1,
        total_weight: 100,
        weight_unit: "kg",
        quantity: 1,
        type: "pallet",
      });
    },

    /** Remove selected cargo unit info */
    onRemoveCargoUnit(cargo_unit) {
      this.quotation.cargo_units = this.quotation.cargo_units.filter(function (ele) {
        return ele != cargo_unit;
      });
    },

    loadOriginCities(quotationType, portCode) {
      var response = null;

      if (quotationType == this.CONSTANT.QUOTATION_TYPE_SEA_FCL) {
        this.$https
          .get("/api/pts/haulage/cities/" + portCode)
          .then((resp) => (response = resp.data))
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            if (response != null && response.status == constant.RESPONSE_STATUS_OK) {
              this.citiesHaulageExport = response.data;
            }
          });
      } else if (
        quotationType == this.CONSTANT.QUOTATION_TYPE_SEA_LCL ||
        quotationType == this.CONSTANT.QUOTATION_TYPE_AIR
      ) {
        this.$https
          .get("/api/pts/transport/cities/" + portCode)
          .then((resp) => (response = resp.data))
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            if (response != null && response.status == constant.RESPONSE_STATUS_OK) {
              this.citiesTransportExport = response.data;
            }
          });
      }
    },

    loadDestinationCities(quotationType, portCode) {
      var response = null;

      if (quotationType == this.CONSTANT.QUOTATION_TYPE_SEA_FCL) {
        this.$https
          .get("/api/pts/haulage/cities/" + portCode)
          .then((resp) => (response = resp.data))
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            if (response != null && response.status == constant.RESPONSE_STATUS_OK) {
              this.citiesHaulageImport = response.data;
            }
          });
      } else if (
        quotationType == this.CONSTANT.QUOTATION_TYPE_SEA_LCL ||
        quotationType == this.CONSTANT.QUOTATION_TYPE_AIR
      ) {
        this.$https
          .get("/api/pts/transport/cities/" + portCode)
          .then((resp) => (response = resp.data))
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            if (response != null && response.status == constant.RESPONSE_STATUS_OK) {
              this.citiesTransportImport = response.data;
            }
          });
      }
    },

    async onRecalculate() {
      this.$root.blockUImsg = "Recalculating financial components";
      this.$root.blockUIshow = true;

      let resp = await this.$https.post(
        `/api/quotations/${this.quotation.quotation_id}/recalculate`,
        this.quotation
      );
      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.quotation = resp.data.data;
        // console.log("data: ", resp.data.data);

        // if there is an extra message about this quotation request
        if (
          this.$root.is_admin &&
          this.quotation.hasOwnProperty("pricing_info") &&
          this.quotation.pricing_info.length > 0
        ) {
          this.quotationRequestMsg = this.quotation.pricing_info;
          this.dialogQuotationRequestMsg = true;
        }
      } else {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;
        console.log(resp.data);
      }
      this.$root.blockUIshow = false;
    },

    onRefreshQty() {
      // perform LCL qty calculation
      if (this.quotation.quotation_type == "LCL") {
        this.getLCLQty();
      } else if (this.quotation.quotation_type == "AIR") {
        this.getAirQty();
      }
    },

    getLCLQty() {
      this.quotation.total_cubic_meters = this.getTotalCubicMeters();
      this.quotation.total_weight = this.getTotalWeightInKilograms();
      this.quotation.total_ton = this.getTotalWeightInTons(this.quotation.total_weight);

      this.quotation.quantity = this.findCargoUnitQuantity(
        this.quotation.total_cubic_meters,
        this.quotation.total_ton
      );

      this.assignComponentQty();
    },

    getAirQty() {
      //* find the chargeable weight to determine the quantity
      // Step 1: Calculate the volume (in cbm) of the air freight shipment
      // calculate the cubic meters and the weights for the cargo units
      this.quotation.total_cubic_meters = this.getTotalCubicMeters();

      // Step 2: Finalise the volumetric weight = CBM * 167
      this.quotation.volumetric_weight = parseFloat(
        this.quotation.total_cubic_meters * constant.AIR_CUBIC_CONVERSION_FACTOR
      );

      // Step 3: Calculate the total weight in kilograms
      this.quotation.total_weight = this.getTotalWeightInKilograms();
      // convert the total weight into tons
      this.quotation.total_ton = this.getTotalWeightInTons(this.quotation.total_weight);

      // Step 4a: Compare the gross weight with the volumetric weight to determine the quantity
      // get the cargo units quatity (chargeable weight)
      this.quotation.quantity = this.findCargoUnitQuantity(
        this.quotation.volumetric_weight,
        this.quotation.total_weight
      );

      // step 4b: Compate the total cubic meters with total to determine the Revenue Tonne quantity
      this.quotation.rt_quantity = this.findCargoUnitQuantity(
        this.quotation.total_cubic_meters,
        this.quotation.total_ton
      );

      this.assignComponentQty();
    },

    assignComponentQty() {
      // repopulate the component quantity based on their UOM
      for (const component of this.quotation.estimate_component) {
        // only update non-zero quantity
        if (component.quantity != 0) {
          // assign quantity
          component.quantity = Math.ceil(this.quotation.quantity);

          // if type is transport, then quantity is always 1 bc by default uom is SET
          if (
            component.type == "export-transport-charges" ||
            component.type == "import-transport-charges"
          ) {
            component.quantity = 1;
          }
          // but if uom is either QTL or C3KG
          if (component.uom == constant.QUINTAL) {
            // divide the kg by 100 or multiply the ton by 10
            component.quantity = Math.ceil(parseFloat(this.quotation.total_ton * 10));
          } else if (component.uom == constant.CBM_3KG) {
            // since 1CBM : 300KG, then find the quintal of the result
            // then find the highest between the C3KG and the total ton
            component.quantity = Math.ceil(
              parseFloat(
                Math.max(this.quotation.total_cubic_meters * 3, this.quotation.total_ton)
              )
            );
          } else if (component.uom == constant.REVENUE_TONNE) {
            // if uom is RT, then use the revenue tonne calculation
            component.quantity = Math.ceil(
              parseFloat(
                Math.max(this.quotation.total_cubic_meters, this.quotation.total_ton)
              )
            );
          } else if (component.uom == constant.UNIT_SET) {
            component.quantity = 1;
          }

          // if uom is KG, then use the total weight
          else if (component.uom == constant.KG) {
            component.quantity = Math.ceil(this.quotation.total_weight);
          }

          // if include_telex_release is false, quantity for LC-TLX = 0
          if (!this.quotation.include_telex_release && component.code == "LC-TLX") {
            component.quantity = 0;
          }
        }
      }
    },

    calculateTotalCubicMeters(cargo_unit) {
      let total_cbm =
        parseFloat(cargo_unit.total_cubic_meters) * parseFloat(cargo_unit.quantity);
      return parseFloat(total_cbm);
    },

    calculateTotalWeightInKilograms(cargo_unit) {
      let weight_unit = cargo_unit.weight_unit;
      let total_weight = parseFloat(cargo_unit.total_weight);
      let quantity = parseFloat(cargo_unit.quantity);

      // if weight unit is not kilograms or pounds, return 0.00
      if (weight_unit != "kg" && weight_unit != "lbs") {
        return 0.0;
      }

      // pounds to kilograms
      let lbs_to_kg = 0.453592;

      // convert total_weight in pounds to kg
      if (weight_unit == "lbs") {
        total_weight *= lbs_to_kg;
      }

      // convert quantity into float for this calculation to prevent an error
      return parseFloat(total_weight * quantity);
    },

    getTotalCubicMeters() {
      let total_cbm = 0.0;

      // loop thru the cargo units
      for (const cargo_unit of this.quotation.cargo_units) {
        total_cbm += this.calculateTotalCubicMeters(cargo_unit);
      }

      return parseFloat(total_cbm.toFixed(2));
    },

    getTotalWeightInKilograms() {
      let total = 0.0;
      // loop thru cargo units
      for (const cargo_unit of this.quotation.cargo_units) {
        total += this.calculateTotalWeightInKilograms(cargo_unit);
      }
      return parseFloat(total.toFixed(2));
    },

    getTotalWeightInTons(total_weight) {
      // kilogram to tonne
      let kg_to_tonne = 0.001;
      return parseFloat(this.quotation.total_weight * kg_to_tonne);
    },

    findCargoUnitQuantity(total_cubic_meters, weight) {
      // compare the cubic meter to weight
      return parseFloat(Math.max(total_cubic_meters, weight));
    },

    /**
     * Add a general item component to FC
     */
    addNewComponent() {
      this.quotation.estimate_component.push({
        type: constant.TYPE_GENERAL_ITEM,
        code: "OT-GEN",
        conversion_factor: 1.0,
        cost: {
          base: 0.0,
          min: 0.0,
          per_unit: 0.0,
          converted: 0.0,
        },
        currency: this.quotation.currency,
        description: "General item",
        price: {
          base: 0.0,
          min: 0.0,
          per_unit: 0.0,
          converted: 0.0,
        },
        quantity: 1,
        uom: "SET",
      });
    },

    /**
     * Add discount/rebate component to FC
     */
    addDiscountComponent() {
      this.quotation.estimate_component.push({
        type: constant.TYPE_DISCOUNT_REBATE,
        code: "OT-REB",
        conversion_factor: 1.0,
        cost: {
          base: 0.0,
          min: 0.0,
          per_unit: 0.0,
          converted: 0.0,
        },
        currency: this.quotation.currency,
        description: "Discount/rebate description",
        price: {
          base: 0.0,
          min: 0.0,
          per_unit: 0.0,
          converted: 0.0,
        },
        quantity: 1,
        uom: "SET",
      });
    },

    /**
     * on open company list
     */
    onOpenCompanyList() {
      this.companyListDialogTitle = "Choose company";
      this.dialogCompanyList = true;
    },

    /** On select and close company list dialog */
    onDialogCompanyListClose(selectedCompany) {
      this.dialogCompanyList = false;
      this.new_quotation_company = selectedCompany;
      this.dialogChangeCompanyWarning = true;
    },

    /**
     * change quotation company
     */
    async changeQuotationCompany() {
      this.$root.blockUImsg = "Changing quotation company";
      this.$root.blockUIshow = true;

      let resp = await this.$https.put(
        `/api/quotations/assign/${this.quotation.quotation_id}/company/${this.new_quotation_company.company_id}`
      );
      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.showSnackbar = true;
        this.snackbarColor = "success";
        this.snackbarMsg = resp.data.msg;
        this.new_quotation_company = null;

        // replace the quotation company
        this.quotation.company = resp.data.data;
        this.quotation.company_id = resp.data.data.company_id;
        this.quotation.currency = resp.data.data.preferred_currency;

        this.isReAssigned = true;
        this.reAssignedCompany = resp.data.data;

        this.dialogChangeCompanyWarning = false;
      } else {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;

        this.alert = true;
        this.alertMessage = resp.data.msg;
        this.alertType = "error";
      }
      this.$root.blockUIshow = false;
    },

    /**
     * Admin create shipment for this quotation before confirmation
     */
    async adminCreateShipment() {
      this.$root.blockUImsg = "Creating shipment";
      this.$root.blockUIshow = true;

      let resp = await this.$https.post(
        `/api/quotations/${this.quotation.quotation_id}/admin_create_shipment`
      );
      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.showSnackbar = true;
        this.snackbarColor = "success";
        this.snackbarMsg = resp.data.msg;
        this.quotation.has_shipment = true;
        this.quotation.admin_override = true;
      } else {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;
        console.log("Error saving quotation", resp.data);
      }

      this.$root.blockUIshow = false;
    },

    //* Haulage Address Mapping

    /** Haulage Exporter's Address autocomplete */
    initHaulageExporterAddressAutocomplete() {
      this.haulage_exporter_autocomplete = new google.maps.places.Autocomplete(
        document.getElementById("haulage-exporter-autocomplete"),
        {
          componentRestrictions: {
            country: this.quotation.origin_port.code_country.toLowerCase(),
          },
        }
      );

      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      this.haulage_exporter_autocomplete.setFields([
        "address_components",
        "formatted_address",
        "geometry",
        "place_id",
        "name",
        "types",
        // "plus_code",
      ]);
      // When the user selects an address from the drop-down, populate the
      // address fields in the form.
      this.haulage_exporter_autocomplete.addListener(
        "place_changed",
        this.haulageExporterFillInAddress
      );
    },

    /** Haulage Importer's Address autocomplete */
    initHaulageImporterAddressAutocomplete() {
      this.haulage_importer_autocomplete = new google.maps.places.Autocomplete(
        document.getElementById("haulage-importer-autocomplete"),
        {
          componentRestrictions: {
            country: this.quotation.destination_port.code_country.toLowerCase(),
          },
        }
      );
      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      this.haulage_importer_autocomplete.setFields([
        "address_components",
        "formatted_address",
        "geometry",
        "place_id",
        "name",
        "types",
        // "plus_code",
      ]);
      // When the user selects an address from the drop-down, populate the
      // address fields in the form.
      this.haulage_importer_autocomplete.addListener(
        "place_changed",
        this.haulageImporterFillInAddress
      );
    },

    /**
     * Haulage Exporter Fill in the address information
     */
    haulageExporterFillInAddress() {
      // Get the place details from the autocomplete object.
      const place = this.haulage_exporter_autocomplete.getPlace();

      // update the city geocode data
      this.quotation.exporter_location.address_components = place.address_components;
      this.quotation.exporter_location.formatted_address = place.formatted_address;
      this.quotation.exporter_location.place_id = place.place_id;
      this.quotation.exporter_location.name = place.name;
      this.quotation.exporter_location.types = place.types;
      this.quotation.exporter_location.geometry = {
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
      };
    },

    /**
     * Haulage Importer Fill in the address information
     */
    haulageImporterFillInAddress() {
      // Get the place details from the autocomplete object.
      const place = this.haulage_importer_autocomplete.getPlace();

      // update the city geocode data
      this.quotation.importer_location.address_components = place.address_components;
      this.quotation.importer_location.formatted_address = place.formatted_address;
      this.quotation.importer_location.place_id = place.place_id;
      this.quotation.importer_location.name = place.name;
      this.quotation.importer_location.types = place.types;
      this.quotation.importer_location.geometry = {
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
      };
    },

    //* Transport Address Mapping

    /** Transport Exporter's Address autocomplete */
    initTransportExporterAddressAutocomplete() {
      this.transport_exporter_autocomplete = new google.maps.places.Autocomplete(
        document.getElementById("transport-exporter-autocomplete"),
        {
          componentRestrictions: {
            country: this.quotation.origin_port.code_country.toLowerCase(),
          },
        }
      );
      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      this.transport_exporter_autocomplete.setFields([
        "address_components",
        "formatted_address",
        "geometry",
        "place_id",
        "name",
        "types",
        // "plus_code",
      ]);
      // When the user selects an address from the drop-down, populate the
      // address fields in the form.
      this.transport_exporter_autocomplete.addListener(
        "place_changed",
        this.transportExporterFillInAddress
      );
    },

    /** Transport Importer's Address autocomplete */
    initTransportImporterAddressAutocomplete() {
      this.transport_importer_autocomplete = new google.maps.places.Autocomplete(
        document.getElementById("transport-importer-autocomplete"),
        {
          componentRestrictions: {
            country: this.quotation.destination_port.code_country.toLowerCase(),
          },
        }
      );
      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      this.transport_importer_autocomplete.setFields([
        "address_components",
        "formatted_address",
        "geometry",
        "place_id",
        "name",
        "types",
        // "plus_code",
      ]);
      // When the user selects an address from the drop-down, populate the
      // address fields in the form.
      this.transport_importer_autocomplete.addListener(
        "place_changed",
        this.transportImporterFillInAddress
      );
    },

    /**
     * Transport Exporter Fill in the address information
     */
    transportExporterFillInAddress() {
      // Get the place details from the autocomplete object.
      const place = this.transport_exporter_autocomplete.getPlace();

      // update the city geocode data
      this.quotation.exporter_location.address_components = place.address_components;
      this.quotation.exporter_location.formatted_address = place.formatted_address;
      this.quotation.exporter_location.place_id = place.place_id;
      this.quotation.exporter_location.name = place.name;
      this.quotation.exporter_location.types = place.types;
      this.quotation.exporter_location.geometry = {
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
      };
    },

    /**
     * Transport Importer Fill in the address information
     */
    transportImporterFillInAddress() {
      // Get the place details from the autocomplete object.
      const place = this.transport_importer_autocomplete.getPlace();

      // update the city geocode data
      this.quotation.importer_location.address_components = place.address_components;
      this.quotation.importer_location.formatted_address = place.formatted_address;
      this.quotation.importer_location.place_id = place.place_id;
      this.quotation.importer_location.name = place.name;
      this.quotation.importer_location.types = place.types;
      this.quotation.importer_location.geometry = {
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
      };
    },

    /**
     * On save updated quotation
     */
    onSave() {
      var response = null;
      this.$root.blockUImsg = "Saving quotation";
      this.$root.blockUIshow = true;

      this.$https
        .put("/api/quotations/" + this.quotation.quotation_id, this.quotation)
        .then((resp) => (response = resp.data))
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          if (response != null && response.status == constant.RESPONSE_STATUS_OK) {
            this.showSnackbar = true;
            this.snackbarColor = "success";
            this.snackbarMsg = response.msg;

            this.updatedQuotation = response.data;
            this.quotationUpdated = true;
          } else {
            this.showSnackbar = true;
            this.snackbarColor = "error";
            this.snackbarMsg = response.msg;
            console.log("Error saving quotation", response);
          }
          this.$root.blockUIshow = false;
        });
    },

    /**
     * On open the message box
     */
    onViewMessages() {
      this.messageBoxTitle = `Quotation ${this.quotation.quotation_id}`;
      this.messageBoxQuotationId = this.quotation.quotation_id;
      this.messageBoxInvoiceId = null;
      this.messageBoxCompanyId = this.quotation.company.company_id;
      this.messageBoxCompanyName = this.quotation.company.name;
      this.messageBoxTags = [
        `${this.quotation.quotation_type.toLowerCase()}`,
        `${this.quotation.transaction_type.toLowerCase()}`,
        `${this.quotation.incoterm.incoterm.toLowerCase()}`,
        `${this.quotation.cargo_type.code.toLowerCase()}`,
      ];
      this.dialogMessageBox = true;
    },

    /**
     * on close comment message dialog
     */
    onDialogCloseMessageBox() {
      this.dialogMessageBox = false;
      this.messageBoxTitle = null;
      this.messageBoxQuotationId = null;
      this.messageBoxInvoiceId = null;
      this.messageBoxCompanyId = null;
      this.messageBoxCompanyName = null;
      this.messageBoxTags = null;
    },

    onDialogClose() {
      this.$emit(
        "dialog-close",
        this.updatedQuotation,
        this.quotationUpdated,
        this.isReAssigned,
        this.reAssignedCompany
      );
    },

    onUpdateStatus() {
      var response = null;
      this.$root.blockUImsg = "Updating quotation status";
      this.$root.blockUIshow = true;

      this.$https
        .put(
          constant.API_HOST +
            "/api/quotations/" +
            this.quotation.quotation_id +
            "/status/" +
            this.quotation.status
        )
        .then((resp) => (response = resp.data))
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          // console.log(response);
          if (response.status == constant.RESPONSE_STATUS_OK) {
            this.showSnackbar = true;
            this.snackbarColor = "success";
            this.snackbarMsg = response.msg;
            this.quotationUpdated = true;
          } else {
            this.showSnackbar = true;
            this.snackbarColor = "error";
            this.snackbarMsg = response.msg;
            console.log("Error updating quotation status", response);
          }
          this.$root.blockUIshow = false;
        });
    },

    onOverviewRefresh() {
      this.quotationOverview = JSON.parse(JSON.stringify(this.quotation));
    },

    /** Get the quotation title to represent the file name */
    getTitleFilename() {
      var quotationIDLabel =
        this.quotation.quotation_id != null ? this.quotation.quotation_id : "DRAFT";

      return (
        quotationIDLabel +
        " " +
        this.quotation.transaction_type +
        " " +
        this.quotation.container_load +
        " " +
        this.quotation.incoterm.incoterm +
        " " +
        this.quotation.cargo_type.code +
        " " +
        this.quotation.origin_port.un_code +
        " to " +
        this.quotation.destination_port.un_code
      );
    },

    /** Download as a pdf file */
    downloadPDF() {
      this.$root.blockUImsg = "Downloading quotation file...";
      this.$root.blockUIshow = true;

      // package the url link
      var url_link = `${constant.HOSTNAME}/print-quotation-overview?id=${this.quotation.quotation_id}`;
      var url = `https://asia-northeast1-cloud-accele-freight.cloudfunctions.net/generatePDF?url_link=${url_link}&fn=${this.getTitleFilename()}`;
      axios
        .get(url, {
          responseType: "blob",
        })
        .then((resp) => {
          var fileURL = URL.createObjectURL(resp.data);
          var fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.download = `${this.getTitleFilename()}.pdf`;
          fileLink.click();

          // revoke the file url to clear up memory
          setTimeout(function () {
            // For Firefox it is necessary to delay revoking the ObjectURL
            URL.revokeObjectURL(fileURL);
          }, 100);

          this.showSnackbar = true;
          this.snackbarColor = "success";
          this.snackbarMsg = "Quotation successfully downloaded";
        })
        .catch((err) => {
          console.log(err);
          this.showSnackbar = true;
          this.snackbarColor = "error";
          this.snackbarMsg = err;
          this.$root.blockUIshow = false;
        })
        .finally(() => {
          this.$root.blockUIshow = false;
        });
    },
  },

  computed: {
    sortedPortList: function () {
      if (this.quotation.quotation_type == constant.QUOTATION_TYPE_AIR) {
        return this.airPortList.sort(function (a, b) {
          return a.label.localeCompare(b.label);
        });
      } else {
        return this.seaPortList.sort(function (a, b) {
          return a.label.localeCompare(b.label);
        });
      }
    },

    sortedIncotermList: function () {
      return this.incotermList.sort(function (a, b) {
        return a.incoterm.localeCompare(b.incoterm);
      });
    },

    sortedTransportExportCitiesList: function () {
      return this.citiesTransportExport.sort(function (a, b) {
        return a.label.localeCompare(b.label);
      });
    },

    sortedTransportImportCitiesList: function () {
      return this.citiesTransportImport.sort(function (a, b) {
        return a.label.localeCompare(b.label);
      });
    },

    sortedHaulageExportCitiesList: function () {
      return this.citiesHaulageExport.sort(function (a, b) {
        return a.label.localeCompare(b.label);
      });
    },

    sortedHaulageImportCitiesList: function () {
      return this.citiesHaulageImport.sort(function (a, b) {
        return a.label.localeCompare(b.label);
      });
    },

    origin_port_watcher() {
      return this.quotation != null ? this.quotation.origin_port : null;
    },

    destination_port_watcher() {
      return this.quotation != null ? this.quotation.destination_port : null;
    },

    show_export_clearance: {
      get() {
        if (
          this.quotation == null ||
          this.quotation.transaction_type == null ||
          Object.keys(this.quotation.incoterm).length === 0
        ) {
          return false;
        }

        return (
          this.quotation.incoterm.responsibilities.export_clearance ==
          this.quotation.transaction_type
        );
      },
    },

    show_import_clearance: {
      get() {
        if (
          this.quotation == null ||
          this.quotation.transaction_type == null ||
          Object.keys(this.quotation.incoterm).length === 0
        ) {
          return false;
        }

        return (
          this.quotation.incoterm.responsibilities.import_clearance ==
          this.quotation.transaction_type
        );
      },
    },

    show_include_telex_release: {
      get() {
        if (
          this.quotation == null ||
          this.quotation.transaction_type == null ||
          Object.keys(this.quotation.incoterm).length === 0
        ) {
          return false;
        }

        return (
          this.quotation.incoterm.responsibilities.export_local_charges ==
          this.quotation.transaction_type
        );
      },
    },

    show_insurance: {
      get() {
        if (
          this.quotation == null ||
          this.quotation.transaction_type == null ||
          Object.keys(this.quotation.incoterm).length === 0
        ) {
          return false;
        }

        return (
          this.quotation.incoterm.responsibilities.insurance ==
          this.quotation.transaction_type
        );
      },
    },

    show_export_haulage: {
      get() {
        if (
          this.quotation == null ||
          this.quotation.transaction_type == null ||
          Object.keys(this.quotation.incoterm).length === 0
        ) {
          return false;
        }

        if (this.quotation.quotation_type != this.CONSTANT.QUOTATION_TYPE_SEA_FCL) {
          return false;
        }

        return (
          this.quotation.incoterm.responsibilities.export_haulage_transport ==
          this.quotation.transaction_type
        );
      },
    },

    show_import_haulage: {
      get() {
        if (
          this.quotation == null ||
          this.quotation.transaction_type == null ||
          Object.keys(this.quotation.incoterm).length === 0
        ) {
          return false;
        }

        if (this.quotation.quotation_type != this.CONSTANT.QUOTATION_TYPE_SEA_FCL) {
          return false;
        }

        return (
          this.quotation.incoterm.responsibilities.import_haulage_transport ==
          this.quotation.transaction_type
        );
      },
    },

    show_export_transport: {
      get() {
        if (
          this.quotation == null ||
          this.quotation.transaction_type == null ||
          Object.keys(this.quotation.incoterm).length === 0
        ) {
          return false;
        }

        if (this.quotation.quotation_type == this.CONSTANT.QUOTATION_TYPE_SEA_FCL) {
          return false;
        }

        return (
          this.quotation.incoterm.responsibilities.export_haulage_transport ==
          this.quotation.transaction_type
        );
      },
    },

    show_import_transport: {
      get() {
        if (
          this.quotation == null ||
          this.quotation.transaction_type == null ||
          Object.keys(this.quotation.incoterm).length === 0
        ) {
          return false;
        }

        if (
          this.quotation == null ||
          this.quotation.quotation_type == this.CONSTANT.QUOTATION_TYPE_SEA_FCL
        ) {
          return false;
        }

        return (
          this.quotation.incoterm.responsibilities.import_haulage_transport ==
          this.quotation.transaction_type
        );
      },
    },
  },
};
</script>

<style>
.inset-row.input-row {
  border: 1px solid #e0e0e0;
  background-color: #f5f5f5;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-right: 0px;
}

.inset-row.input-row .input-group {
  width: auto;
  min-width: 150px;
  padding-right: 0;
  margin-right: 30px;
}

.v-tabs.dm-quotation-tabs {
  max-height: 50px;
}
</style>
